import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import {
  listAssessments,
  publishAssessment,
  closeAssessment,
  duplicateAssessment,
  archiveAssessment,
} from '../../api/assessmentApi';
import Badge from '../../components/ui/Badge';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';

const STATUS_TABS = ['all', 'draft', 'published', 'closed', 'archived'];

const STATUS_VARIANT = {
  draft:     'default',
  published: 'success',
  closed:    'warning',
  archived:  'error',
};

const PAGE_SIZE = 15;

function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-card bg-white shadow-xl ring-1 ring-neutral-200 p-6">
        <p className="text-sm text-neutral-700">{message}</p>
        <div className="mt-5 flex justify-end gap-3">
          <button type="button" className="btn-secondary text-sm" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn-danger text-sm" onClick={onConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AssessmentListPage() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [confirm, setConfirm] = useState(null); // { message, onConfirm }
  const searchTimer = useRef(null);

  const fetchAssessments = useCallback((q, tab, p) => {
    setLoading(true);
    const params = { page: p, per_page: PAGE_SIZE };
    if (q) params.search = q;
    if (tab !== 'all') params.status = tab;
    listAssessments(params)
      .then((res) => {
        const d = res.data;
        setAssessments(d.data ?? []);
        setTotal(d.meta?.total ?? d.total ?? 0);
      })
      .catch(() => toast.error('Failed to load assessments'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPage(1);
      fetchAssessments(search, activeTab, 1);
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [search, activeTab, fetchAssessments]);

  useEffect(() => {
    fetchAssessments(search, activeTab, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handlePublish = async (id) => {
    try {
      await publishAssessment(id);
      toast.success('Assessment published');
      fetchAssessments(search, activeTab, page);
    } catch {
      toast.error('Failed to publish assessment');
    }
  };

  const handleClose = async (id) => {
    try {
      await closeAssessment(id);
      toast.success('Assessment closed');
      fetchAssessments(search, activeTab, page);
    } catch {
      toast.error('Failed to close assessment');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await duplicateAssessment(id);
      toast.success('Assessment duplicated');
      const newId = res.data?.data?.id;
      if (newId) navigate(`/assessments/${newId}`);
      else fetchAssessments(search, activeTab, page);
    } catch {
      toast.error('Failed to duplicate assessment');
    }
  };

  const handleArchive = (id) => {
    setConfirm({
      message: 'Are you sure you want to archive this assessment? It will no longer be accessible to participants.',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await archiveAssessment(id);
          toast.success('Assessment archived');
          fetchAssessments(search, activeTab, page);
        } catch {
          toast.error('Failed to archive assessment');
        }
      },
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Confirm dialog */}
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-neutral-900 font-heading">Assessments</h1>
        <Link to="/assessments/new" className="btn-primary text-sm self-start sm:self-auto">
          <PlusIcon className="h-4 w-4" />
          New Assessment
        </Link>
      </div>

      {/* Search + filter tabs */}
      <div className="rounded-card bg-white shadow-card ring-1 ring-neutral-200/60 overflow-hidden">
        <div className="px-4 pt-4 pb-0 border-b border-neutral-100 space-y-3">
          {/* Search */}
          <div className="relative max-w-sm">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search assessments…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-base pl-9 text-sm"
            />
          </div>
          {/* Status tabs */}
          <div className="flex gap-1 overflow-x-auto">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => { setActiveTab(tab); setPage(1); }}
                className={clsx(
                  'px-4 py-2 text-sm font-medium capitalize rounded-t-md border-b-2 transition-colors whitespace-nowrap',
                  activeTab === tab
                    ? 'border-primary-600 text-primary-600 bg-primary-50/60'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
                )}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" className="text-primary-600" />
          </div>
        ) : assessments.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={ClipboardDocumentListIcon}
              title="No assessments found"
              description={
                search || activeTab !== 'all'
                  ? 'Try adjusting your search or filter.'
                  : 'Get started by creating your first assessment.'
              }
              action={
                activeTab === 'all' && !search ? (
                  <Link to="/assessments/new" className="btn-primary text-sm">
                    <PlusIcon className="h-4 w-4" />
                    New Assessment
                  </Link>
                ) : null
              }
            />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-neutral-100">
                <thead>
                  <tr className="bg-neutral-50">
                    {['Title', 'Status', 'Bootcamp', 'Questions', 'Participants', 'Created', ''].map(
                      (h, i) => (
                        <th
                          key={i}
                          className={clsx(
                            'py-3 text-xs font-semibold text-neutral-500 uppercase tracking-wide',
                            i === 0 ? 'px-6 text-left' : 'px-4',
                            i >= 3 && i <= 4 ? 'text-right' : 'text-left',
                            i === 6 ? 'text-right pr-6' : ''
                          )}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-50 bg-white">
                  {assessments.map((a) => (
                    <tr key={a.id} className="hover:bg-neutral-50 transition-colors group">
                      <td className="px-6 py-3.5 max-w-xs">
                        <Link
                          to={`/assessments/${a.id}`}
                          className="font-medium text-neutral-900 text-sm hover:text-primary-600 truncate block"
                        >
                          {a.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3.5">
                        <Badge variant={STATUS_VARIANT[a.status] ?? 'default'}>
                          {a.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-neutral-500">
                        {a.bootcamp_id ?? '—'}
                      </td>
                      <td className="px-4 py-3.5 text-right text-sm text-neutral-600">
                        {a.questions_count ?? a.question_count ?? 0}
                      </td>
                      <td className="px-4 py-3.5 text-right text-sm text-neutral-600">
                        {a.participant_count ?? a.participants_count ?? 0}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-neutral-500">
                        {a.created_at
                          ? new Date(a.created_at).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <Menu as="div" className="relative inline-block text-left">
                          <Menu.Button className="btn-ghost p-1.5 rounded-lg opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                            <EllipsisVerticalIcon className="h-5 w-5" />
                            <span className="sr-only">Actions</span>
                          </Menu.Button>
                          <Menu.Items className="absolute right-0 z-10 mt-1 w-48 origin-top-right rounded-card bg-white shadow-lg ring-1 ring-neutral-200 focus:outline-none divide-y divide-neutral-100">
                            <div className="py-1">
                              <Menu.Item>
                                {({ active }) => (
                                  <Link
                                    to={`/assessments/${a.id}`}
                                    className={clsx(
                                      'block px-4 py-2 text-sm text-neutral-700',
                                      active && 'bg-neutral-50'
                                    )}
                                  >
                                    View Detail
                                  </Link>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <Link
                                    to={`/assessments/${a.id}/edit`}
                                    className={clsx(
                                      'block px-4 py-2 text-sm text-neutral-700',
                                      active && 'bg-neutral-50'
                                    )}
                                  >
                                    Edit
                                  </Link>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleDuplicate(a.id)}
                                    className={clsx(
                                      'block w-full text-left px-4 py-2 text-sm text-neutral-700',
                                      active && 'bg-neutral-50'
                                    )}
                                  >
                                    Duplicate
                                  </button>
                                )}
                              </Menu.Item>
                            </div>
                            <div className="py-1">
                              {a.status === 'draft' && (
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      type="button"
                                      onClick={() => handlePublish(a.id)}
                                      className={clsx(
                                        'block w-full text-left px-4 py-2 text-sm text-success-700',
                                        active && 'bg-neutral-50'
                                      )}
                                    >
                                      Publish
                                    </button>
                                  )}
                                </Menu.Item>
                              )}
                              {a.status === 'published' && (
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      type="button"
                                      onClick={() => handleClose(a.id)}
                                      className={clsx(
                                        'block w-full text-left px-4 py-2 text-sm text-warning-700',
                                        active && 'bg-neutral-50'
                                      )}
                                    >
                                      Close
                                    </button>
                                  )}
                                </Menu.Item>
                              )}
                              {a.status !== 'archived' && (
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      type="button"
                                      onClick={() => handleArchive(a.id)}
                                      className={clsx(
                                        'block w-full text-left px-4 py-2 text-sm text-error-700',
                                        active && 'bg-neutral-50'
                                      )}
                                    >
                                      Archive
                                    </button>
                                  )}
                                </Menu.Item>
                              )}
                            </div>
                          </Menu.Items>
                        </Menu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-neutral-100 px-6 py-3">
                <p className="text-sm text-neutral-500">
                  Page {page} of {totalPages} &mdash; {total} total
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="btn-secondary text-sm disabled:opacity-40"
                  >
                    ← Prev
                  </button>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="btn-secondary text-sm disabled:opacity-40"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
